import React, { useEffect, useState } from 'react';
import { View, Modal } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { accessService, InvalidIpAddress, IpAddressAlreadyExists } from '../../../services/central-api/access';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { getFormattedAvailableIPCount } from '../../../utils/getAvailableIpsCount';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    allowedIpId?: number;
}

export default function AllowedIpModal({ onClose, allowedIpId, isModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [ipAddress, setIpAddress] = useState('');
    const [description, setDescription] = useState<string>('');
    const [accessGrantedIps, setAccessGrantedIps] = useState<string>('');

    async function getAllowedIp(id: number) {
        const installationCompany = await accessService.getAllowedIp(id);
        setIpAddress(installationCompany.ipAddress);
        setDescription(installationCompany.description);
    }

    async function loadPageInfo() {
        try {
            if (allowedIpId) {
                await getAllowedIp(allowedIpId);
            }
        } catch (err) {
            console.error(err);
        }
    }

    function isIpAddressInvalid(value: string) {
        return value == '' || accessGrantedIps == 'Invalid';
    }

    function isDescriptionInvalid(value: string) {
        return value == '';
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    useEffect(() => {
        if (ipAddress) {
            const availableIps = getFormattedAvailableIPCount(ipAddress);
            setAccessGrantedIps(availableIps === 'Invalid' ? translate(availableIps) : availableIps);
        }
    }, [ipAddress]);

    function isFormValid() {
        return !isIpAddressInvalid(ipAddress) && !isDescriptionInvalid(description);
    }

    function clearValues() {
        setIpAddress('');
        setDescription('');
    }

    async function formSubmit() {
        try {
            if (allowedIpId) {
                await accessService.updateAllowedIp({
                    id: allowedIpId,
                    ipAddress: ipAddress,
                    description: description
                });
            } else {
                await accessService.createAllowedIp({ ipAddress, description });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: `${translate('ActionSuccessfully')}\n\n${translate('actionWillTakeFewMinutes')}`,
                visibilityTime: 10000,
            });
        } catch (err) {
            if (err instanceof InvalidIpAddress || err instanceof IpAddressAlreadyExists) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message, { ipAddress: err.ipAddress }),
                    visibilityTime: 10000,
                });
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            clearValues();
            onClose();
        }
    }

    return (
        <Modal transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                clearValues();
                onClose();
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    <View style={{ rowGap: 10 }}>
                        <FormInput
                            label={translate('ipAddress')}
                            placeholder={translate('ipAddress')}
                            value={ipAddress}
                            onChangeText={setIpAddress}
                            invalid={isIpAddressInvalid}
                        />
                        <FormInput
                            label={translate('description')}
                            placeholder={translate('description')}
                            value={description}
                            onChangeText={setDescription}
                            invalid={isDescriptionInvalid}
                            multiline={true}
                            numberOfLines={3}
                        />
                        <FormInput
                            label={translate('accessGrantedIps')}
                            placeholder={translate('accessGrantedIps')}
                            value={accessGrantedIps}
                            onChangeText={setAccessGrantedIps}
                            disabled={true}
                        />
                    </View>
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            clearValues();
                            onClose();
                        }}
                        disabled={!isFormValid()} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 400,
        minHeight: 200,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
}));
